import { ReactNode, useState, useRef, useEffect } from "react";
import { InfoCircleFilled, QuestionCircleFilled } from "@ant-design/icons";
import classNames from "classnames";
import styles from "./index.module.scss";

interface ITooltipProps {
  children: ReactNode;
  placement?: "top" | "left" | "right" | "bottom" | "bottomRight";
  iconType?: "info" | "question";
  iconColor?: "default" | "info" | "question";
  textAlign?:
    | "start"
    | "end"
    | "left"
    | "right"
    | "center"
    | "justify"
    | "match-parent";
  isShowCard?: boolean;
  padding?: string;
  innerWidth?: string;
  prevText?: string;
  nextText?: string;
}

const IconColorMapper: Record<string, string> = {
  default: "#1a1a1a",
  info: "#0049b0",
  question: "#b5bbc2",
};

const Tooltip = ({
  children,
  placement = "top",
  iconType = "info",
  iconColor = "default",
  textAlign = "center",
  innerWidth = "100%",
  isShowCard = true,
  padding = "5px",
  prevText,
  nextText,
}: ITooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 마우스 진입 시 툴팁 표시
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(true);
  };

  // 마우스 이탈 시 툴팁 숨김
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  // 외부 클릭 시 툴팁 숨김
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderIcon = () => {
    const iconStyle = { color: IconColorMapper[iconColor], cursor: "default" };

    if (iconType === "info") {
      return <InfoCircleFilled rev="" style={iconStyle} />;
    }
    if (iconType === "question") {
      return <QuestionCircleFilled rev="" style={iconStyle} />;
    }
    return <InfoCircleFilled rev="" style={iconStyle} />;
  };

  const renderTooltipContent = () => {
    const content = isShowCard ? (
      <div
        className={styles.tooltipCard}
        style={{ textAlign, width: innerWidth, padding }}
      >
        {children}
      </div>
    ) : (
      <span style={{ textAlign, width: innerWidth, padding }}>{children}</span>
    );

    return (
      <div
        className={classNames(
          styles.tooltipContent,
          styles[placement],
          isVisible && styles.visible
        )}
      >
        {content}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={styles.tooltipContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.tooltipTrigger}>
        {prevText && <span className={styles.prevText}>{prevText}</span>}
        <span className={classNames(styles.icon, styles[iconColor])}>
          {renderIcon()}
        </span>
        {nextText && <span className={styles.nextText}>{nextText}</span>}
      </div>
      {renderTooltipContent()}
    </div>
  );
};

export default Tooltip;
